import { Alert, message, Modal, Tabs } from "antd";
import React from "react";
import { LoginForm } from "./LoginForm";
import { RegisterForm } from "./RegisterForm";

const TabPane = Tabs.TabPane;

export interface LoginModalProps {
  /** Is the modal visible? */
  visible: boolean;
  /** Called to hide the modal */
  onHide(): void;
  /** Called on successful login */
  onLogin(
    userId: number,
    fullName: string,
    email: string,
    isAdmin: boolean
  ): void;
}

export interface LoginModalState {
  // Error message for login form
  loginError?: string;
  // Error message for registration form
  registrationError?: string;
}

/**
 * Small container component to display the Login/Register forms in a modal
 * @see LoginForm
 * @see RegisterForm
 * TODO Write as functional component, it's really simple
 */
export class LoginModal extends React.Component<
  LoginModalProps,
  LoginModalState
> {
  constructor(props: LoginModalProps) {
    super(props);

    // No errors!
    this.state = {
      loginError: undefined,
      registrationError: undefined,
    };
  }

  public render(): JSX.Element {
    const privacyNoticeText = (
      <div>
        <p>
          By creating an account you consent to the gathering and storing of the
          following information:
        </p>
        <ol>
          <li>IP Address (authentication)</li>
          <li>E-mail (authentication)</li>
          <li>full name (identification)</li>
          <li>everything you submit using the contribution forms</li>
        </ol>
        <p>
          Additionally, by logging in, you consent to the use of cookies. You
          can use the website without any data collection by not creating an
          account.
        </p>
      </div>
    );

    return (
      <Modal
        bodyStyle={{
          display: "flex",
          flexFlow: "column nowrap",
          alignItems: "stretch",
        }}
        visible={this.props.visible}
        title={null}
        footer={null}
        centered={true}
        width={450}
        onCancel={this.handleCancel}
      >
        <Tabs defaultActiveKey="login" style={{ marginBottom: "1em" }}>
          <TabPane tab="Login" key="login">
            <div
              style={{
                display: "flex",
                flexFlow: "column nowrap",
                alignItems: "stretch",
              }}
            >
              <LoginForm
                onLogin={this.props.onLogin}
                onError={this.handleLoginError}
              />
              {this.state.loginError !== undefined && (
                <Alert
                  type="error"
                  showIcon={true}
                  message={this.state.loginError}
                />
              )}
            </div>
          </TabPane>

          <TabPane tab="Register" key="register">
            <div
              style={{
                display: "flex",
                flexFlow: "column nowrap",
                alignItems: "stretch",
              }}
            >
              <RegisterForm
                onRegister={this.handleRegistration}
                onError={this.handleRegistrationError}
              />
              {this.state.registrationError !== undefined && (
                <Alert
                  type="error"
                  showIcon={true}
                  message={this.state.registrationError}
                />
              )}
            </div>
          </TabPane>
        </Tabs>

        <Alert
          type="info"
          showIcon={true}
          message="Privacy Notice"
          description={privacyNoticeText}
        />
      </Modal>
    );
  }

  private handleCancel = (): void => this.props.onHide();

  private handleLoginError = (
    error: "wrong-password" | "inactive-account" | "server-error"
  ): void => {
    console.error(`Server responded ${error} to login request`);
    switch (error) {
      case "wrong-password":
        this.setState({
          loginError: "Wrong email address or password!",
        });
        return;
      case "inactive-account":
        this.setState({
          loginError:
            "Your user account is not activated. You will be notified by email when it's ready.",
        });
        return;
      case "server-error":
        this.setState({
          loginError: "Could not login! Connection or server error.",
        });
        return;
    }
  };

  private handleRegistration = (): void => {
    message.success(
      "Your account has been created. You will be notified by e-mail when it's activated!"
    );
    this.props.onHide();
  };

  private handleRegistrationError = (
    error: "account-already-exists" | "failure" | "connection-error"
  ): void => {
    let registrationError: string;

    switch (error) {
      case "account-already-exists":
        registrationError = "An account already exists with this email address";
        break;
      case "failure":
        registrationError =
          "You account could not be created, please try again later.";
        break;
      case "connection-error":
        registrationError = "Could not complete request, network error.";
        break;
    }

    this.setState({ registrationError });
  };
}
